<template>
    <div class="google-calendar-integration">
        <div class="block rounded-24 mb-1">
            <div class="block-header pt-1">
                <h3 class="text-center block-title fs-5 fw-semibold text-light-blue">
                    {{ $t('configuration.integration.googleCalendarParams') }}
                </h3>
            </div>

            <div class="row justify-content-center pt-4">
                <div class="mt-3">
                    <div v-if="!isConnected" class="row">
                        <div class="col-md-12 d-flex justify-content-center">
                            <button
                                @click="authorize"
                                class="btn btn-success rounded-8 fw-semibold px-4 py-2"
                                style="color: white"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    class="me-2"
                                    viewBox="0 0 16 16"
                                >
                                    <path
                                        d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z"
                                    />
                                </svg>
                                {{ $t('configuration.integration.authorizeGoogle') }}
                            </button>
                        </div>
                    </div>
                    <div v-else>
                        <span class="badge badge-success px-3 py-2 d-flex justify-content-center mb-3">
                            <i class="fa fa-check-circle me-1"></i>{{ $t('configuration.integration.googleConnected') }}
                        </span>
                        <div class="d-flex justify-content-center">
                            <button
                                @click="authorize"
                                class="btn btn-success rounded-8 fw-semibold px-4 py-2"
                                style="color: white"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    class="me-2"
                                    viewBox="0 0 16 16"
                                >
                                    <path
                                        d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z"
                                    />
                                </svg>
                                {{ $t('configuration.integration.linkAnotherAccount') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row justify-content-center pt-4">
                <div class="col-md-8 roles-field">
                    <label class="fs-6 fw-medium text_dark_blue mb-2">
                        {{ $t('configuration.integration.selectRolesToNotify') }} <span class="text-purple">*</span>
                    </label>
                    <BaseSelect
                        v-model="selectedRoles"
                        :options="rolesOptions"
                        option-label="label"
                        option-value="value"
                        :multiple="true"
                        :searchable="true"
                        :placeholder="$t('configuration.integration.selectRolesPlaceholder')"
                        fixed-dropdown
                        auto-flip
                    />
                    <p v-if="selectedRoles.length === 0" class="roles-hint mt-2 mb-0">
                        {{ $t('configuration.integration.noRoleSelectedHint') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BaseSelect from '../../../Shared/BaseSelect.vue'

export default {
    name: 'GoogleCalendarIntegration',
    components: {
        BaseSelect,
    },
    props: {
        integration: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            selectedRoles: [],
            loading: false,
        }
    },
    computed: {
        roles() {
            return this.integration.roles || []
        },
        rolesOptions() {
            // La valeur est l'id du rôle : l'intégrité est garantie côté base par la clé
            // étrangère de google_calendar_setting_role (ON DELETE CASCADE), plus par une
            // chaîne de noms — dont le séparateur virgule était ambigu.
            return this.roles.map((role) => ({
                value: role.id,
                label: role.display_name,
            }))
        },
        config() {
            return this.integration.config || {}
        },
        isConnected() {
            // L'état de connexion est décidé côté serveur : le front ne voit plus
            // les jetons OAuth.
            return Boolean(this.integration.active)
        },
    },
    created() {
        // Tolère les DEUX formats le temps d'une release : le serveur renvoie désormais un
        // tableau d'IDs, mais un backend non encore déployé (ou un rollback PHP) renvoie
        // encore la chaîne « 2,4,5 ». C'est la discipline expand/migrate/contract que le
        // dépôt applique déjà aux colonnes de base, transposée à un contrat JSON consommé
        // par un bundle susceptible d'être désynchronisé. À retirer à la release suivante.
        const raw = this.config.roles_ids

        if (Array.isArray(raw)) {
            this.selectedRoles = raw.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))
        } else if (typeof raw === 'string' && raw !== '') {
            this.selectedRoles = raw
                .split(',')
                .map((id) => parseInt(id, 10))
                .filter((id) => !isNaN(id))
        }
    },
    methods: {
        authorize() {
            // Replicate the form submission from Blade - use the correct route
            const form = document.createElement('form')
            form.method = 'POST'
            form.action = '/back-office/website/google-calendar/initiate'
            form.setAttribute('data-turbo', 'false')

            // Add CSRF token
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
            if (csrfToken) {
                const input = document.createElement('input')
                input.type = 'hidden'
                input.name = '_token'
                input.value = csrfToken
                form.appendChild(input)
            }

            this.selectedRoles.forEach((roleId) => {
                const input = document.createElement('input')
                input.type = 'hidden'
                input.name = 'roles_ids[]'
                input.value = roleId
                form.appendChild(input)
            })

            document.body.appendChild(form)
            form.submit()
        },
        async saveRoles() {
            this.loading = true
            try {
                const response = await axios.post('/back-office/v3/integration/save-google-calendar-roles', {
                    roles_ids: this.selectedRoles,
                })

                if (response.data.success) {
                    this.$emit('success', response.data.message)
                } else {
                    this.$emit('error', response.data.message)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.$emit('error', this.$t('common.error_occurred'))
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.google-calendar-integration {
    padding: 20px;
}

.width-btn {
    min-width: 150px;
}

/*
 * Habillage des puces de rôles, SCOPÉ à ce composant.
 *
 * Volontairement ici et pas dans `BaseSelect` : ce dernier est partagé par une trentaine
 * d'écrans, une retouche globale de `.vs__selected` les modifierait tous. `::v-deep` est
 * nécessaire pour atteindre les internes de `vue-select`, qui est un composant enfant —
 * c'est d'ailleurs pourquoi la règle `.vs__selected` de `BaseSelect` (sans `::v-deep`,
 * ligne 436) ne s'applique à rien.
 */
.roles-field ::v-deep .vs__selected {
    background-color: #f3eafa; /* primary.100 */
    border: 1px solid #dbc1f0; /* primary.200 */
    border-radius: 8px;
    color: #571f84 !important; /* primary.700 — lisible sur fond clair */
    padding: 2px 8px !important;
    margin: 2px 4px 2px 0 !important;
    font-size: 13px;
    line-height: 1.4;
}

/* La croix hérite du violet et rougit au survol, comme les suppressions du BO V3. */
.roles-field ::v-deep .vs__deselect {
    fill: #7d2dbd;
    margin-left: 6px;
    margin-right: 0;
    transition: fill 0.15s ease;
}

.roles-field ::v-deep .vs__selected:hover .vs__deselect {
    fill: #e11d48;
}

/* Bordure du champ alignée sur les autres champs V3 (cf. CustomInput). */
.roles-field ::v-deep .vs__dropdown-toggle {
    border-radius: 12px;
    border: 1px solid #9c9af880;
    min-height: 44px;
    padding: 4px 6px;
}

.roles-field ::v-deep .v-select.vs--open .vs__dropdown-toggle {
    border-color: #7d2dbd;
    box-shadow: 0 0 0 3px rgba(125, 45, 189, 0.12);
}

.roles-hint {
    font-size: 12px;
    color: #94a3b8;
}
</style>
